import { Injectable } from '@angular/core';
import { CoachBranding } from '@cf-shared/data-model/coach';
import { UserDb } from 'services/user-db.service';
import { LoggingService } from 'services/logging.service';
import { CoachBrandingThemeService } from 'services/coach-branding-theme.service';
import { CoachBrandingState, resolveActiveBranding } from 'services/coach-branding-state';

/**
 * Coach white-labeling (#3707) as seen by the app shell: which coach brand is
 * active for the signed-in user, and the logo / brand name the shell renders.
 * It picks the branding; the separate `CoachBrandingThemeService` applies it.
 * Why the two are separate is written out in design-docs/coach-white-labeling.md.
 *
 * Lifted out of `AppComponent` with no behavior change. The boot sequence still
 * owns WHEN these run — see design-docs/boot-sequence.md.
 */
@Injectable({ providedIn: 'root' })
export class CoachBrandingStateService extends CoachBrandingState {
  constructor(
    theme: CoachBrandingThemeService,
    private userDb: UserDb,
    private logging: LoggingService,
  ) {
    super(theme);
  }

  /**
   * Theme the app from the active branding. Resolves the branding from the
   * current userDb and hands it to the theme service. Safe to call on every boot
   * path and after manage-mode transitions. When there is no branding (or an
   * invalid accent), the service reverts to MH.
   */
  applyActiveBranding(): void {
    this.applyBrandingValue(this.resolveActiveBranding());
  }

  /**
   * Re-theme the app when the active branding document changes. Called from the
   * boot DocWatcher on every DB change; the change guard lives in the base class.
   */
  reapplyActiveBrandingIfChanged(): void {
    this.reapplyIfChanged(this.resolveActiveBranding());
  }

  /**
   * Read the two coach-relationship singletons and pick the branding to show.
   * The picking, and the fall back to MH when a read throws, live in the plain
   * half beside this file so they can be unit tested; this method is only the
   * wiring that supplies the user-database reads and the problem reporter.
   */
  private resolveActiveBranding(): CoachBranding | undefined {
    return resolveActiveBranding(
      {
        asClientBranding: () => this.userDb.loadCoachRelAsClient()?.branding,
        asCoachBranding: () => this.userDb.loadCoachRelAsCoach()?.branding,
        isManagingClient: () => !!this.userDb.getCoachManagingClient(),
      },
      (name, context) => this.logging.trackSilentProblem(name, context),
    );
  }
}
